import { Button } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { EmptyState } from '../components/EmptyState';
import { canAccess } from './access';
import { sectionById, type SectionId } from './sections';
import { useSession } from './session';

/** Plain "You don't have access" state (never a 404) for a section the role doesn't include. */
export function NoAccess({ section }: { section: SectionId }) {
  const { user } = useSession();
  const label = sectionById(section).label;
  return (
    <EmptyState
      variant="warn"
      icon={<IconLock size={28} stroke={2} />}
      title={`You don't have access to ${label}`}
      body={`It isn't part of the ${user.roleTitle} role. If you need it for your job, ask your branch manager.`}
      action={
        <Button component={Link} to="/" variant="default">
          Back to Home
        </Button>
      }
    />
  );
}

interface RequireSectionProps {
  id: SectionId;
  /** Omit to render the nested <Outlet />. */
  children?: ReactNode;
}

/** Route guard: renders the section's routes only when the signed-in person can reach it. */
export function RequireSection({ id, children }: RequireSectionProps) {
  const { access } = useSession();
  if (!canAccess(access, id)) return <NoAccess section={id} />;
  return children ? <>{children}</> : <Outlet />;
}
